import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';

/**
 * AttentionCard — single-tile primitive for the KR "Attention Required" band.
 *
 * Compact, scannable, executive-signal card. Renders a colored left-border
 * accent, an icon chip, a big count, a short explanation, and (when a route
 * exists) a "View" action link. Severity drives the accent color only —
 * the card layout is intentionally uniform so the eye compares counts, not
 * chrome.
 *
 * Props:
 *   title       — short block name (e.g. "Missing Information")
 *   count       — number OR undefined (undefined ⇒ '—')
 *   description — one-line explanation of what needs action
 *   icon        — lucide icon component
 *   href        — optional deep link; when set, a "View" affordance renders
 *   actionLabel — override for the default "View →" text (optional)
 *   severity    — 'critical' | 'warning' | 'info' | 'neutral' (default 'info')
 *   testId      — data-testid on the outer card
 */
const SEVERITY_STYLES = {
  critical: {
    accent: 'border-l-rose-500',
    chip: 'bg-rose-50 text-rose-600',
    count: 'text-rose-700',
    action: 'text-rose-600 hover:text-rose-700',
  },
  warning: {
    accent: 'border-l-amber-500',
    chip: 'bg-amber-50 text-amber-600',
    count: 'text-amber-700',
    action: 'text-amber-600 hover:text-amber-700',
  },
  info: {
    accent: 'border-l-sky-500',
    chip: 'bg-sky-50 text-sky-600',
    count: 'text-sky-700',
    action: 'text-sky-600 hover:text-sky-700',
  },
  neutral: {
    accent: 'border-l-slate-400',
    chip: 'bg-slate-100 text-slate-600',
    count: 'text-slate-800',
    action: 'text-sky-600 hover:text-sky-700',
  },
};

export const AttentionCard = ({
  title,
  count,
  description,
  icon: Icon,
  href,
  actionLabel = 'View',
  severity = 'info',
  testId,
}) => {
  const s = SEVERITY_STYLES[severity] || SEVERITY_STYLES.info;

  return (
    <Card
      className={`animate-slideIn card-hover border-l-4 ${s.accent}`}
      data-testid={testId}
    >
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className={`inline-flex items-center justify-center w-9 h-9 rounded-lg ${s.chip} shrink-0`}>
            {Icon && <Icon size={18} />}
          </div>
          <span className={`text-3xl font-heading font-semibold tabular-nums leading-none ${s.count}`}>
            {count ?? '—'}
          </span>
        </div>
        <p className="text-sm font-semibold text-slate-900 mb-1">{title}</p>
        {description && (
          <p className="text-xs text-slate-500 line-clamp-2">{description}</p>
        )}
        {href && (
          <Link
            to={href}
            className={`mt-3 inline-flex items-center gap-1 text-xs font-medium ${s.action}`}
            data-testid={testId ? `${testId}-link` : undefined}
          >
            {actionLabel} <ArrowRight size={12} />
          </Link>
        )}
      </CardContent>
    </Card>
  );
};

export default AttentionCard;
